import {Rental} from '../../../../common/types/rental'
import {formatDate, parseISO} from '../../../../common/utils/dateUtils'

export interface RentalsPerDayData {
  date: string
  numberOfRentals: number
  numberOfRentalsRollingAverage: number
}

/** Convenience alias for chart points (same shape). */
export type RentalsPerDayPoint = RentalsPerDayData

export interface RentalsPerDay {
  date: string
  numberOfRentals: number
}

const generateDateRange = (start: Date, end: Date): string[] => {
  const dates: string[] = []
  const currentDate = new Date(start)

  while (currentDate <= end) {
    dates.push(formatDate(new Date(currentDate)))
    currentDate.setDate(currentDate.getDate() + 1)
  }

  return dates
}

const mapRentalsToDateCounts = (rentals: Rental[]): Record<string, number> => {
  return rentals.reduce(
    (acc, rental) => {
      const rentalDate = formatDate(parseISO(rental.rentStart))
      acc[rentalDate] = (acc[rentalDate] || 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )
}

const calculateRollingAverage = (rentalsMap: Record<string, number>, dates: string[], index: number, windowSize: number): number => {
  const pastDates = dates.slice(Math.max(0, index - windowSize + 1), index + 1)

  const totalRentals = pastDates.reduce((sum, date) => {
    return sum + (rentalsMap[date] || 0)
  }, 0)

  return totalRentals / pastDates.length
}

export const generateRentalsPerDayData = (rentals: Rental[], fromDate: string, toDate: string, rollingDaysAverage: number): RentalsPerDayData[] => {
  const rentalsPerDayMap = mapRentalsToDateCounts(rentals)
  const allDates = generateDateRange(parseISO(fromDate), parseISO(toDate))

  return allDates.map((date, index) => ({
    date,
    numberOfRentals: rentalsPerDayMap[date] || 0,
    numberOfRentalsRollingAverage: calculateRollingAverage(rentalsPerDayMap, allDates, index, rollingDaysAverage),
  }))
}

export const calculateMeanAverage = (data: RentalsPerDay[], currentPos: number, windowSize: number): number => {
  let total = 0
  for (let i = 0; i < windowSize; i++) {
    const day = data[currentPos - i]
    if (day) {
      total += day.numberOfRentals
    }
  }
  return total / Math.min(currentPos + 1, windowSize)
}
